import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { FileDown, BookOpen } from "lucide-react";
import { fetchMeta } from "@/lib/api";
import { Reveal, SplitLines } from "@/components/Reveal";
import { CatalogueGate } from "@/components/CatalogueGate";

const COVERS = ["/images/tiles/calacatta.jpg", "/images/tiles/nero.jpg", "/images/tiles/statuario.jpg", "/images/tiles/concrete.jpg", "/images/tiles/terrazzo.jpg", "/images/tiles/oak.jpg"];

export const Catalogues = () => {
  const { data: meta } = useQuery({ queryKey: ["meta"], queryFn: fetchMeta });
  const list = meta?.catalogues || [];
  const [active, setActive] = useState(null);
  return (
    <section id="catalogues" data-testid="catalogues-section" className="bg-[var(--bg-soft)] py-28 lg:py-36">
      <div className="container-x">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <Reveal><p className="eyebrow mb-6">E-Catalogue</p></Reveal>
            <SplitLines as="h2" inView className="font-display text-3xl sm:text-4xl lg:text-5xl font-light leading-[1.05] tracking-tight text-[var(--navy)]" lines={["Download our", <>digital <em className="italic text-[var(--mint)]">catalogues.</em></>]} />
          </div>
          <Reveal delay={0.2}><p className="max-w-sm text-sm leading-relaxed text-[var(--muted)]">Full-resolution PDF brochures for every format, with room renders, technical data and packing details.</p></Reveal>
        </div>
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((c, i) => (
            <motion.button
              type="button"
              key={c.slug}
              data-testid={`catalogue-${c.slug}`}
              onClick={() => setActive(c)}
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-5% 0px" }} transition={{ duration: 0.8, delay: i * 0.08 }}
              className="group relative flex items-stretch gap-5 overflow-hidden rounded-[1.5rem] border border-slate-200 bg-white p-4 text-left transition-shadow duration-500 hover:shadow-[0_30px_60px_-30px_rgba(19,34,56,.4)]"
            >
              <div className="relative w-28 shrink-0 overflow-hidden rounded-xl shadow-[8px_8px_0_0_var(--teal-soft)]">
                <img src={COVERS[i % COVERS.length]} alt={c.title} className="h-full w-full object-cover transition-transform duration-1000 group-hover:scale-110" />
                <div className="absolute inset-x-0 bottom-0 bg-[var(--navy)]/85 px-2 py-1.5 font-mono text-[0.5rem] uppercase tracking-widest text-white">{c.year} Edition</div>
              </div>
              <div className="flex flex-1 flex-col justify-between py-1">
                <div>
                  <div className="font-mono text-[0.58rem] uppercase tracking-[0.25em] text-[var(--mint)]">Catalogue 0{i + 1}</div>
                  <h3 className="mt-2 font-display text-xl font-medium text-[var(--navy)]">{c.title}</h3>
                  <p className="mt-1 flex items-center gap-1.5 text-xs text-[var(--muted)]"><BookOpen size={12} /> {c.pages} pages · PDF</p>
                </div>
                <span className="inline-flex items-center gap-2 text-xs font-semibold text-[var(--navy)] group-hover:text-[var(--mint)] transition-colors"><FileDown size={14} /> Download</span>
              </div>
            </motion.button>
          ))}
        </div>
      </div>
      <CatalogueGate catalogue={active} onClose={() => setActive(null)} />
    </section>
  );
};
